import type { Project } from '../types/projects'
import { getProjectImages } from '../utils/images'

/*
  TODO(abraham): the case-study fields below (`year`, `role`, `problem`,
  `outcome`) are the ones that make the work index and detail pages read like
  case studies instead of a list of titles. They are intentionally left blank
  where I don't know the real answer — the UI hides any field that's missing,
  so fill them in as you go and they'll appear. `outcome` is the one worth
  most: a number beats an adjective every time.
*/

// Map project IDs to their image folder names
const projectImageFolders: Record<string, string> = {
  'chinet-mobile': 'chinet',
  'raaz-tms': 'raaz',
  'hidyat-rider': 'hidyat-rider',
  'hidyat-driver': 'hidyat-driver',
  'hidyat-rental': 'hidyat-rental',
  'neoconnect-mobile': 'neoconnect',
  'native-location-tracker': 'location-tracker',
  'expense-manager': 'expense-manager',
  'campus-docs-mobile': 'campus-docs',
  'moya-mobile': 'moya',
  'unreel': 'unreel',
  'neo-pharmacy': 'pms',
  'neo-ems': 'ems',
  'newtab-extension': 'tilio',
  'locale-editor': 'locale-editor'
}

/*
  The lead image is deliberately the FIRST image, not a random one: the hero
  scene is a composed layout, and a poster that changes on every reload makes
  it impossible to design against (and shifts the page between reloads).
*/
const getImagesForProject = (projectId: string): { image?: string; images?: string[] } => {
  const folderName = projectImageFolders[projectId]
  if (!folderName) return {}

  const images = getProjectImages(folderName)

  return {
    image: images[0],
    images: images.length > 0 ? images : undefined
  }
}

export const projects: Project[] = [
  {
    id: 'chinet-mobile',
    title: 'ChiNet - Logistics & Freight Marketplace',
    shortTitle: 'ChiNet Mobile',
    description: 'Production Flutter mobile platform connecting shippers, carriers, and brokers across Ethiopia with live bidding and secure escrow.',
    longDescription: 'ChiNet is a production-grade Flutter logistics application engineered for shippers, carriers, and freight brokers in Ethiopia. It features real-time carrier loadboards, automated bidding systems, interactive escrow wallet management with hold statements, and turn-by-turn shipment tracking. Built with strict mobile security standards using Google Play Integrity and FreeRASP runtime protection, automated SMS OTP retrieval, and resilient background GPS tracking.',
    technologies: ['Flutter', 'Dart', 'GetX', 'Google Maps', 'Dio', 'FreeRASP', 'Play Integrity', 'Socket.IO', 'In-App Update'],
    features: [
      'Multi-role dashboards for shippers, carriers, and freight brokers',
      'Live loadboard with real-time shipment search, filtering, and counter-offers',
      'Secure escrow wallet with hold statements and bank transaction history',
      'Zero-permission SMS OTP autofill using Google SMS Retriever API',
      'Enterprise mobile application security attestation with FreeRASP and Play Integrity',
      'Real-time GPS shipment tracking with offline queue buffering',
      'Native in-app update flows with custom branded update dialogs',
      'In-app ad delivery network with custom creative card widgets and impression tracking'
    ],
    github: 'https://github.com/ChiNet-Platforms/chinet-mobile',
    demo: 'https://play.google.com/store/apps/details?id=com.chinet.chinet',
    ...getImagesForProject('chinet-mobile'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      '80+ production releases with Play Store distribution',
      'Enterprise device security with Play Integrity & FreeRASP',
      'Zero-data-loss background GPS tracking',
      'Custom Flutter animations & custom painter loaders'
    ],
    year: '2025 – 2026',
    role: 'Lead Mobile Engineer',
    problem: 'Fragmented freight matching and lack of transparent tracking and secure escrow payments for Ethiopian logistics operators.',
    outcome: 'Delivered a high-retention Flutter production app serving thousands of shipments with automated bidding and secure escrow.'
  },
  {
    id: 'raaz-tms',
    title: 'RaAz TMS - Transport & Fleet Management',
    shortTitle: 'RaAz TMS',
    description: 'Offline-first React Native mobile application for fleet operators, drivers, and dispatchers with live trip tracking.',
    longDescription: 'RaAz TMS is an offline-first mobile application built with React Native and Expo for enterprise transport and fleet operations. It enables dispatchers and fleet managers to coordinate multi-customer cargo shipments, track driver assignments in real time, and log maintenance schedules. Designed with a local-first Realm database architecture to ensure uninterrupted operation in low-connectivity remote corridors, with automatic background synchronization when network restores.',
    technologies: ['React Native', 'Expo', 'TypeScript', 'Realm', 'Zustand', 'TanStack Query', 'Google Maps', 'Socket.IO', 'Reanimated'],
    features: [
      'Offline-first local database caching and synchronization with Realm',
      'Real-time vehicle GPS trip tracking and route replay with Google Maps',
      'Multi-role & multi-customer workflow architecture',
      'Shipment status lifecycle management and digital proof-of-delivery capture',
      'Vehicle maintenance scheduling, fault reporting, and service logs',
      'Custom typed design system supporting light and dark modes',
      'Push notifications via Firebase Cloud Messaging for urgent dispatch alerts'
    ],
    github: 'https://github.com/RAAZ-TECH-2024/tms-mobile',
    demo: 'https://play.google.com/store/apps/details?id=com.raaztech.chinet',
    ...getImagesForProject('raaz-tms'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Offline-first architecture with Realm DB',
      'Real-time GPS trip tracking & route mapping',
      'Comprehensive multi-role fleet oversight',
      'Smooth 60fps animations with React Native Reanimated'
    ],
    year: '2024 – 2026',
    role: 'Lead Mobile Engineer',
    problem: 'Fleet operators lacked offline-capable mobile tools for real-time trip monitoring, driver dispatch, and maintenance tracking in low-connectivity regions.',
    outcome: 'Architected an offline-first mobile TMS ensuring 100% field operational uptime and live fleet oversight.'
  },
  {
    id: 'hidyat-rider',
    title: 'Hidyat RIDE - Electric Vehicle Ride-Hailing Platform',
    shortTitle: 'Hidyat RIDE',
    description: 'Production Flutter ride-hailing app for electric vehicles with real-time driver tracking, automated dispatch, and multi-language support.',
    longDescription: 'Hidyat RIDE is a green electric vehicle (EV) ride-hailing mobile platform in Ethiopia. Built with Flutter, Hooks Riverpod, and Socket.IO, it delivers real-time bidirectional driver location tracking, automated trip fare estimation, live route navigation with Google Maps Flutter, Firebase push notifications, and localized experiences in English, Amharic, and Tigrigna using Slang i18n.',
    technologies: ['Flutter', 'Dart', 'Hooks Riverpod', 'Socket.IO', 'Google Maps', 'Dio', 'Firebase Messaging', 'Slang i18n', 'Secure Storage'],
    features: [
      'Interactive map with real-time nearby EV vehicle markers and live tracking',
      'Instant ride request booking with automated driver dispatch matching',
      'Dynamic upfront fare calculator and trip duration estimations',
      'Multilingual interface supporting English, Amharic, and Tigrigna (Slang i18n)',
      'Push notification alerts for driver arrival, trip start, and completion',
      'Secure credential and session storage using Flutter Secure Storage',
      'Offline-resilient networking architecture with Dio and connectivity interceptors'
    ],
    github: 'https://github.com/Hdyat-General-Trading-PLC/hdyat-mobile-rider',
    demo: 'https://play.google.com/store/apps/details?id=com.hidyat.rider',
    ...getImagesForProject('hidyat-rider'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Live Google Play Store distribution for Ethiopia EV fleet',
      'Sub-second driver GPS synchronization over Socket.IO',
      'Modular multi-package architecture via hdyat-mobile-common',
      'Full bilingual localization (English, Amharic, Tigrigna)'
    ],
    year: '2025 – 2026',
    role: 'Lead Mobile Engineer',
    problem: 'Commuters needed a modern, localized EV ride-hailing experience with live driver tracking and transparent fare calculation.',
    outcome: 'Delivered an EV ride-hailing mobile app serving thousands of urban rides with real-time WebSocket vehicle tracking.'
  },
  {
    id: 'hidyat-driver',
    title: 'Hidyat DRIVER - Fleet & Ride Dispatch System',
    shortTitle: 'Hidyat DRIVER',
    description: 'Production Flutter driver companion app for EV fleet operators with turn-by-turn routing and real-time earnings analytics.',
    longDescription: 'Hidyat DRIVER is the companion mobile application for professional EV drivers on the Hidyat ride-hailing platform. It equips drivers with instant dispatch ride offer acceptance, turn-by-turn pickup and dropoff routing, in-ride lifecycle state management, shift toggles, and detailed daily/weekly earnings and commission reports.',
    technologies: ['Flutter', 'Dart', 'Riverpod', 'Socket.IO', 'Google Maps', 'Dio', 'Foreground Service', 'Background Location', 'Slang i18n'],
    features: [
      'Instant dispatch offer overlay with pickup distance, destination, and estimated fare',
      'Turn-by-turn turn navigation integration with real-time waypoint tracking',
      'Trip lifecycle management: Arrived, Started, In-Transit, Completed',
      'Real-time background GPS coordinate streaming to riders and dispatch consoles',
      'Driver earnings dashboard with daily, weekly, and per-trip breakdown',
      'Online/Offline shift status toggle with battery level monitoring'
    ],
    github: 'https://github.com/Hdyat-General-Trading-PLC/hdyat-mobile-driver',
    demo: 'https://play.google.com/store/apps/details?id=com.hidyat.driver',
    ...getImagesForProject('hidyat-driver'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Live Google Play Store driver companion app',
      'Continuous background GPS telemetry with foreground notification',
      'Instant WebSocket dispatch matching',
      'Comprehensive financial earnings and payout tracking'
    ],
    year: '2025 – 2026',
    role: 'Lead Mobile Engineer',
    problem: 'EV fleet drivers needed an intuitive tool for dispatch matching, route navigation, and transparent commission and earnings tracking.',
    outcome: 'Engineered a resilient driver app with background telemetry and instant dispatch matching across the fleet.'
  },
  {
    id: 'hidyat-rental',
    title: 'Hidyat Rental - Electric Vehicle Rental & Leasing',
    shortTitle: 'Hidyat Rental',
    description: 'Flutter cross-platform vehicle rental mobile app with catalog browsing, duration booking, and KYC document upload.',
    longDescription: 'Hidyat Rental is a digital vehicle rental and leasing application for Hidyat General Trading PLC. It allows customers to browse available EV fleet models, check real-time availability, book rentals by hour or day, upload driver license and identity verification documents, and review digital rental agreements.',
    technologies: ['Flutter', 'Dart', 'Riverpod', 'Dio', 'Slang i18n', 'FilePicker', 'Secure Storage', 'Clean Architecture'],
    features: [
      'Interactive EV vehicle catalog with specifications, battery range, and pricing',
      'Flexible booking duration picker (hourly, daily, weekly)',
      'Digital KYC document verification with driver license & ID upload via FilePicker',
      'Integrated reservation management and upcoming rental schedule tracking',
      'Multi-language support for English and Amharic with Slang'
    ],
    github: 'https://github.com/Hdyat-General-Trading-PLC/hidyat-rental',
    ...getImagesForProject('hidyat-rental'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Clean multi-package architecture sharing hdyat-mobile-common',
      'Digital KYC document upload pipeline',
      'Type-safe Riverpod state management'
    ],
    year: '2026',
    role: 'Lead Mobile Engineer',
    problem: 'Manual paperwork and vehicle reservation delays in traditional car rental workflows in Ethiopia.',
    outcome: 'Architected a modern Flutter rental app digitizing vehicle bookings, KYC verification, and contract management.'
  },
  {
    id: 'neoconnect-mobile',
    title: 'NeoConnect - Telemedicine & Healthcare Platform',
    shortTitle: 'NeoConnect',
    description: 'React Native telemedicine application with WebRTC video consultations, real-time doctor chat, multi-clinic patient portal, and digital wallet.',
    longDescription: 'NeoConnect is a mobile healthcare platform built with React Native and Expo, providing dual-role telemedicine experiences for patients and medical practitioners across multiple clinics. Features real-time WebRTC audio and video consultations with CallKeep integration, live chat via Socket.IO, symptom logging, digital medical records, clinic-scoped health wallets with subscription management, and biometric authentication.',
    technologies: ['React Native', 'Expo', 'TypeScript', 'WebRTC', 'Socket.IO', 'Zustand', 'TanStack Query', 'Reanimated', 'FlashList', 'Firebase Messaging'],
    features: [
      'Dual-role context switching between patient portal and doctor consultation modes',
      'Low-latency WebRTC video and audio telemedicine calls with native CallKeep integration',
      'Real-time instant messaging and AI health assistant integration over Socket.IO',
      'Clinic-scoped digital wallet with balance management and subscription passes',
      'Medical history timeline, symptom tracking, and digital prescription review',
      'High-performance 60fps lists with Shopify FlashList and Reanimated 3',
      'Secure biometric authentication and session management'
    ],
    github: 'https://github.com/Neo-Tech-PLC/neoconnect-mobile',
    ...getImagesForProject('neoconnect-mobile'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Native WebRTC video calling engine',
      'Real-time Socket.IO chat & doctor signaling',
      'Dual-role doctor & patient clinic architecture',
      'Smooth 60fps animations with Reanimated 3 & FlashList'
    ],
    year: '2026',
    role: 'Lead Mobile Engineer',
    problem: 'Fragmented patient-doctor communication, lack of accessible remote consultations, and paper-based clinic record tracking.',
    outcome: 'Built an all-in-one telemedicine mobile platform supporting real-time video consultations and seamless multi-clinic management.'
  },
  {
    id: 'native-location-tracker',
    title: 'Native Background Location Tracker Plugin',
    shortTitle: 'Location Plugin',
    description: 'Native-first background location tracking plugin for Flutter with on-device SQLite buffer and zero data loss.',
    longDescription: 'A robust, open-source background location tracking plugin for Flutter apps on Android and iOS. Built specifically for logistics, fleet management, and ride-hailing applications that demand reliable GPS coordinates even when the app is backgrounded or terminated. Features a native SQLite buffer that queues points during network drops, motion-state heuristic pacing (still/walking/driving), adaptive power/sampling modes, and native 401 token refresh in the background.',
    technologies: ['Flutter', 'Dart', 'Kotlin', 'Swift', 'Android Foreground Service', 'iOS BGTaskScheduler', 'SQLite', 'MethodChannel'],
    features: [
      'Native on-device SQLite queuing guaranteeing zero data loss during offline periods',
      'Motion-state heuristic pacing (speed-based on Android, CMMotionActivity on iOS)',
      'Adaptive sampling adjusting GPS interval based on speed and battery level',
      'Native-side HTTP batch upload with exponential backoff retry',
      'Native 401 token refresh allowing background uploads after Flutter engine is killed',
      'Foreground service notifications on Android with custom deep linking intents'
    ],
    github: 'https://github.com/Abmichael/native_location_tracker',
    ...getImagesForProject('native-location-tracker'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Zero-data-loss SQLite queue architecture',
      'Native Android Foreground Service & iOS BGTaskScheduler',
      'Native token rotation during background execution',
      'Active production use in enterprise logistics apps'
    ],
    year: '2026',
    role: 'Author & Maintainer',
    problem: 'Standard Flutter geolocation plugins drop GPS points or get terminated by the OS when the app is backgrounded or encounters network dead zones.',
    outcome: 'Built a resilient native plugin guaranteeing 100% background GPS capture and zero data loss in production.'
  },
  {
    id: 'expense-manager',
    title: 'Money Manager - Offline-First Android App',
    shortTitle: 'Money Manager',
    description: 'Lean, offline-first Android expense tracker with automated Ethiopian bank SMS parsing and signed rule packs.',
    longDescription: 'Money Manager is a native Android personal finance application built with Kotlin and Jetpack Compose. Built around an offline-first architecture with Room SQLite and sub-unit integer money math (santim), it eliminates floating-point rounding errors. Includes a background SMS transaction monitor that automatically parses messages from Ethiopian banks (CBE, Awash, Telebirr, Anbesa, Wegagen) into suggested transactions, with cryptographically signed parser rule packs updatable over the air.',
    technologies: ['Kotlin', 'Jetpack Compose', 'Material 3', 'Room SQLite', 'StateFlow', 'WorkManager', 'Foreground Services'],
    features: [
      'Automatic SMS transaction parsing for CBE, Awash, Telebirr, Anbesa, and Wegagen',
      'Cryptographically signed parser rule packs for dynamic over-the-air updates',
      'High-precision integer money calculation in minor units (santim)',
      'Interactive monthly overview calendar with daily expense/income drill-down',
      'Category and account analytics breakdown with proportional visualization',
      'Home screen balance summary widget (RemoteViews) and ongoing monitor banner',
      'Offline-first database with Room and reactive StateFlow streams'
    ],
    github: 'https://github.com/Abmichael/expense-manager',
    demo: 'https://play.google.com/store/apps/details?id=com.abe.moneymanager',
    ...getImagesForProject('expense-manager'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      '100% Native Kotlin & Jetpack Compose',
      'Automated banking SMS parsing engine with signed rule envelopes',
      'Sub-millisecond offline Room database queries',
      'Custom home screen widget & background Foreground Service'
    ],
    year: '2026',
    role: 'Solo Developer',
    problem: 'Manual expense tracking is tedious and friction-heavy, while international finance apps cannot parse local Ethiopian banking SMS notifications.',
    outcome: 'Created an automated, privacy-first mobile tracker achieving 99%+ SMS parsing accuracy without internet requirements.'
  },
  {
    id: 'campus-docs-mobile',
    title: 'Campus Docs - Academic Document Workflow',
    shortTitle: 'Campus Docs',
    description: 'Cross-platform Flutter app for university letter issuance, transcript requests, and document verification.',
    longDescription: 'A cross-platform Flutter application backed by an Express API that streamlines academic document requests for universities. Students and alumni can submit transcript and certificate requests, track verification progress in real time, while staff and administrators can generate official letters using dynamic placeholder templates with instant PDF rendering.',
    technologies: ['Flutter 3', 'Dart', 'Riverpod', 'GoRouter', 'Dio', 'Express.js', 'PDF Generation'],
    features: [
      'Role-based user experiences for students, alumni, faculty, and administrators',
      'Dynamic letter templates with placeholder variable replacement and PDF generation',
      'End-to-end transcript and degree request tracking workflow',
      'Reactive state management with Flutter Riverpod and declarative GoRouter',
      'Cross-platform support for Android, iOS, and Web'
    ],
    github: 'https://github.com/Abmichael/campus-docs-mobile',
    ...getImagesForProject('campus-docs-mobile'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Clean Riverpod architecture',
      'Automated PDF generation engine',
      'Cross-platform Flutter 3 UI',
      'Role-based document workflow'
    ],
    year: '2025',
    role: 'Full-Stack Mobile Developer',
    problem: 'University students and alumni faced long waiting times and paper delays when requesting academic transcripts and official letters.',
    outcome: 'Built an automated mobile request workflow cutting document turnaround time from weeks to hours.'
  },
  {
    id: 'moya-mobile',
    title: 'Moya Mobile Application',
    shortTitle: 'Moya',
    description: 'React Native mobile application built with Expo, featuring modern authentication, push notifications, and clean design system.',
    longDescription: 'A professional React Native mobile application following modern development practices and a comprehensive design system. Features user authentication with form validation, Firebase Cloud Messaging integration with Notifee for rich notifications, Redux state management, and a clean UI following brand guidelines with Deep Ocean Blue primary colors and Inter font family.',
    technologies: ['React Native', 'Expo', 'TypeScript', 'Redux Toolkit', 'React Navigation', 'Firebase', 'Notifee', 'React Hook Form', 'Yup'],
    features: [
      'User registration and authentication with validation',
      'Firebase Cloud Messaging with rich notifications',
      'Interactive notification actions and deep linking',
      'Redux state management with typed hooks',
      'Modern design system with 5 preset themes',
      'Form validation with react-hook-form and Yup',
      'Responsive layouts with proper spacing',
      'Smooth animations and transitions'
    ],
    github: 'https://github.com/abraham-michael/moya-mobile',
    demo: 'https://play.google.com/store/apps/details?id=com.neotech.moya',
    ...getImagesForProject('moya-mobile'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Firebase push notifications with Notifee',
      'Comprehensive design system',
      'Full TypeScript coverage',
      'Modern React Native best practices'
    ],
    year: '2025',
    role: 'Lead Mobile Developer',
    problem: 'Need for a brand-aligned, high-fidelity mobile app with reliable push notification handling and seamless user onboarding.',
    outcome: 'Built and delivered a feature-rich mobile app with zero crash rate and real-time push messaging.'
  },
  {
    id: 'unreel',
    title: 'Unreel - Social Video Analytics',
    shortTitle: 'Unreel',
    description: 'Mobile application for social video analytics and discovery across platforms with offline support and custom collections.',
    longDescription: 'Unreel is a React Native mobile application built with Expo that provides analytics and discovery tools for social video content. Track performance metrics across multiple platforms, discover trending content, and manage video collections in one place. Features cross-platform analytics consolidation, content discovery with filtering, custom collections for saved videos, offline downloads, and adaptive light/dark mode theming.',
    technologies: ['React Native', 'Expo', 'TypeScript', 'Expo Router', 'Redux Toolkit', 'RTK Query'],
    features: [
      'Cross-platform analytics consolidation',
      'Content discovery and trending video browsing',
      'Custom collections for organizing videos',
      'Download videos for offline viewing',
      'Light and dark mode with theme toggle',
      'File-system based routing with Expo Router v3',
      'Redux state management with RTK Query',
      'Responsive UI with custom components'
    ],
    github: 'https://github.com/unreel-plc/Unreel-rn-app',
    ...getImagesForProject('unreel'),
    status: 'completed',
    category: 'Mobile',
    highlights: [
      'Multi-platform video analytics',
      'Expo Router v3 file-system routing',
      'RTK Query for API management',
      'Offline-capable with downloads'
    ],
    year: '2025',
    role: 'Mobile Engineer',
    problem: 'Content creators struggled to aggregate video engagement metrics across multiple streaming channels into a single coherent mobile app.',
    outcome: 'Shipped a fast mobile dashboard with unified cross-platform metrics and offline collection caching.'
  },
  {
    id: 'neo-pharmacy',
    title: 'Neo Pharmacy Management System',
    shortTitle: 'Neo PMS',
    description: 'Comprehensive multitenant cloud-based pharmacy management system with POS, inventory management, and analytics.',
    longDescription: 'A full-stack pharmacy management system built with Vue 3 and TypeScript that streamlines pharmacy operations through three distinct user interfaces: Pharmacist Interface for point-of-sale and medication dispensing, Admin Console for comprehensive pharmacy management, and Super Admin Portal for multi-pharmacy oversight and subscription management. Features include real-time inventory tracking, automated alerts, sales analytics, and offline PWA capabilities.',
    technologies: ['Vue 3', 'TypeScript', 'Vite', 'Pinia', 'Tailwind CSS', 'Reka UI', 'VeeValidate', 'Zod', 'TanStack Table', 'Unovis'],
    features: [
      'Multi-tenant architecture with role-based access control',
      'Point of Sale system with barcode scanning',
      'Real-time inventory management with low stock alerts',
      'Comprehensive reporting and analytics dashboard',
      'Purchase order management and audit trails',
      'PWA with offline capabilities',
      'Subscription and payment management',
      'Multi-pharmacy oversight for super admins'
    ],
    github: 'https://github.com/abraham-michael/neo-pharmacy',
    ...getImagesForProject('neo-pharmacy'),
    status: 'completed',
    category: 'Healthcare',
    highlights: [
      'Multitenant cloud-based system',
      'PWA-enabled with offline support',
      'Real-time inventory and sales tracking',
      'Comprehensive role-based access control'
    ],
    year: '2025',
    role: 'Lead Full-Stack Engineer',
    problem: 'Pharmacies struggled with inaccurate stock counts, manual prescription dispensing, and lack of multi-branch revenue visibility.',
    outcome: 'Streamlined pharmacy checkouts and reduced inventory reconciliation time by over 70% across partner locations.'
  },
  {
    id: 'neo-ems',
    title: 'NeoEMS - Healthcare Management System',
    shortTitle: 'NeoEMS',
    description: 'Comprehensive electronic medical system for hospitals featuring patient management, clinical modules, and administrative functions.',
    longDescription: 'NeoEMS is a complete digital healthcare solution built with Vue 3 and TypeScript, providing role-based modules for healthcare professionals and administrative staff. The system manages patient records, medical consultations, laboratory tests, imaging services, pharmacy operations, billing, and includes real-time chat functionality. Features dedicated portals for doctors, nurses, lab technicians, radiologists, pharmacists, and administrative staff.',
    technologies: ['Vue 3', 'TypeScript', 'Vite', 'Pinia', 'Vue Router', 'Tailwind CSS', 'Reka UI', 'VeeValidate', 'Zod', 'Socket.IO', 'TinyMCE', 'Unovis'],
    features: [
      'Patient registration and medical records management',
      'Doctor portal with consultations and prescriptions',
      'Nurse station with triage and vital signs tracking',
      'Laboratory test management and reporting',
      'Radiology with imaging archival and reporting',
      'Pharmacy inventory and medication management',
      'Comprehensive billing and invoice system',
      'Real-time chat communication',
      'Advanced analytics dashboard',
      'Multi-department and ward management'
    ],
    github: 'https://github.com/abraham-michael/neo-ems',
    ...getImagesForProject('neo-ems'),
    status: 'completed',
    category: 'Healthcare',
    highlights: [
      'Complete hospital management solution',
      'Role-based portals for all healthcare staff',
      'Real-time communication with Socket.IO',
      'Rich text editor for medical reports'
    ],
    year: '2025',
    role: 'Lead Architect & Full-Stack Engineer',
    problem: 'Hospitals faced fragmented paper record systems, communication silos between wards, and delayed patient diagnosis flows.',
    outcome: 'Unified hospital departments into a single real-time clinical platform with complete EHR and laboratory tracking.'
  },
  {
    id: 'locale-editor',
    title: 'i18n JSON Editor with Analytics',
    shortTitle: 'i18n Editor',
    description: 'Powerful translation editor with comprehensive analytics, duplicate detection, and quality scoring for multi-locale JSON files.',
    longDescription: 'A sophisticated React-based translation editor that provides deep insights into JSON translation files. Features include a visual translation editor, multi-locale support, real-time validation, and a comprehensive analytics dashboard. The analytics engine performs duplicate detection with fuzzy matching, consistency analysis across locales, quality metrics with heuristic scoring, and interactive visualizations. Calculates quality scores based on completeness, consistency, duplicate avoidance, and common key coverage.',
    technologies: ['React 19', 'TypeScript', 'Vite', 'Recharts', 'Fuzzysort', 'Tailwind CSS'],
    features: [
      'Visual translation editor with multi-locale support',
      'Real-time validation for missing and extra keys',
      'Comprehensive analytics dashboard with quality scoring',
      'Smart duplicate detection with fuzzy matching',
      'Consistency analysis for capitalization and punctuation',
      'Format variable validation across locales',
      'Length variation analysis and coverage tracking',
      'Interactive charts (Bar, Pie, Radar, Line)',
      'Dark mode support',
      'File system integration with save/download'
    ],
    github: 'https://github.com/Abmichael/i18n-editor',
    demo: 'https://locale-editor.abrahamdev.com',
    ...getImagesForProject('locale-editor'),
    status: 'completed',
    category: 'Tools',
    highlights: [
      'Advanced fuzzy matching algorithms',
      'Quality score calculation (0-100)',
      'Levenshtein distance for similarity',
      'Heuristic pattern detection'
    ],
    year: '2025',
    role: 'Creator & Maintainer',
    problem: 'Managing complex multi-language JSON files across internationalized apps frequently produces missing keys, typos, and duplicate strings.',
    outcome: 'Created a visual editor that automates translation linting and calculates instant quality scoring for engineering teams.'
  },
  {
    id: 'newtab-extension',
    title: 'Tilio - Chrome New Tab Extension',
    shortTitle: 'Tilio',
    description: 'Android-style new tab Chrome extension with beautiful folders, custom themes, and drag-and-drop organization.',
    longDescription: 'Transform your Chrome new tab into an organized workspace with this lightweight extension featuring Android-style folders for bookmarks and favorite sites. Built with vanilla JavaScript for maximum performance, it includes 5 beautiful preset themes, configurable grid layouts, drag-and-drop organization, import/export functionality, and an optional clock widget. Works completely offline with local storage and loads in under 100ms.',
    technologies: ['Vanilla JavaScript', 'CSS Grid', 'Chrome Storage API', 'Manifest V3', 'HTML5', 'CSS3'],
    features: [
      'Android-style folder organization system',
      'Drag and drop for easy reorganization',
      '5 beautiful preset themes with customization',
      'Configurable grid size and tile dimensions',
      'Optional digital clock widget',
      'Import/export configuration backup',
      'Completely offline-first with local storage',
      'Lightweight and optimized (under 100ms load time)',
      'Responsive design for all screen sizes',
      'No external requests or data collection'
    ],
    github: 'https://github.com/Abmichael/newtab-extension',
    demo: 'https://chromewebstore.google.com/detail/cbfgabnpppgagkljioahiahmedmebnli?utm_source=item-share-cb',
    ...getImagesForProject('newtab-extension'),
    status: 'completed',
    category: 'Extension',
    highlights: [
      'Load time under 100ms',
      'Privacy-focused with no data collection',
      'Manifest V3 compliant',
      'Available on Chrome Web Store'
    ],
    year: '2025',
    role: 'Creator',
    problem: 'Cluttered browser bookmark bars and slow, ad-heavy new tab replacement extensions.',
    outcome: 'Shipped a sub-100ms Manifest V3 extension to the Chrome Web Store with Android-style folders and 100% offline privacy.'
  }
]

// Technology categories for filtering
export const techCategories = [
  'All',
  'Flutter',
  'React Native',
  'Kotlin',
  'Jetpack Compose',
  'Dart',
  'TypeScript',
  'Vue 3',
  'React',
  'Expo',
  'GetX',
  'Riverpod',
  'Realm',
  'Room SQLite',
  'Redux Toolkit',
  'Pinia',
  'Tailwind CSS'
]

// Project categories
export const projectCategories = [
  'All',
  'Mobile',
  'Healthcare',
  'Tools',
  'Extension'
]

// Project status options
export const projectStatuses = [
  'All',
  'Completed',
  'In Progress',
  'Planned'
]

